import React, { useEffect, useMemo, useRef, useState } from "react";

// Generic themed dropdown that matches OrgFilter, for the cascading scope filters
// (location / department / event / Vasana type). Options are [{ id, name }].
// `disabled` renders an inert trigger — used when a parent scope isn't chosen yet.
export default function PickFilter({
  options,
  value,
  onChange,
  icon = "ph-funnel",
  allLabel = "All",
  placeholder = "Search…",
  disabled = false,
  disabledLabel,
  searchable = true,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    function onDoc(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    function onKey(e) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const selectedLabel = disabled
    ? disabledLabel || allLabel
    : value
    ? options.find((o) => String(o.id) === String(value))?.name || allLabel
    : allLabel;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter((o) => (o.name || "").toLowerCase().includes(q)) : options;
  }, [options, query]);

  function pick(v) {
    onChange(v);
    setOpen(false);
    setQuery("");
  }

  return (
    <div className="es-orgdd" ref={ref}>
      <button
        type="button"
        className={"es-orgdd__trigger" + (open ? " is-open" : "") + (disabled ? " is-disabled" : "")}
        onClick={() => !disabled && setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
      >
        <i className={"ph " + icon + " es-orgdd__icon"}></i>
        <span className="es-orgdd__label">{selectedLabel}</span>
        <i className="ph ph-caret-down es-orgdd__caret"></i>
      </button>

      {open && !disabled && (
        <div className="es-orgdd__panel" role="listbox">
          {searchable && (
            <div className="es-orgdd__searchwrap">
              <i className="ph ph-magnifying-glass"></i>
              <input
                className="es-orgdd__search"
                placeholder={placeholder}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                autoFocus
              />
            </div>
          )}
          <div className="es-orgdd__list">
            <button
              type="button"
              className={"es-orgdd__opt" + (!value ? " is-active" : "")}
              onClick={() => pick("")}
            >
              {allLabel}
            </button>
            {filtered.map((o) => (
              <button
                key={o.id}
                type="button"
                className={"es-orgdd__opt" + (String(o.id) === String(value) ? " is-active" : "")}
                onClick={() => pick(String(o.id))}
              >
                {o.name}
              </button>
            ))}
            {filtered.length === 0 && <div className="es-orgdd__empty">No matches</div>}
          </div>
        </div>
      )}
    </div>
  );
}
